import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// Mirror of the DB enum public.patient_message_category. Patient-facing UI
// only ever picks one of the first three; 'general' exists as the legacy
// default for messages that pre-date categorization.
export type MessageCategory = "medical" | "support" | "billing" | "general";

export interface PatientMessageRow {
  id: string;
  patient_id: string;
  direction: "outbound" | "inbound";
  body: string;
  body_html: string | null;
  subject: string | null;
  status: string;
  read_at: string | null;
  sent_by_user_id: string | null;
  sender_role: "patient" | "care_team" | "provider" | "system";
  sender_display_name: string | null;
  sender_provider_id: string | null;
  category: MessageCategory;
  created_at: string;
}

/**
 * List the patient's full message thread, ordered oldest → newest so the
 * Messages UI can render a chronological scroll. RLS enforces that
 * patients only see their own rows.
 */
export function useMessages(patientId: string | undefined) {
  return useQuery<PatientMessageRow[]>({
    queryKey: ["messages", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("patient_messages")
        .select(
          "id, patient_id, direction, body, body_html, subject, status, read_at, sent_by_user_id, sender_role, sender_display_name, sender_provider_id, category, created_at",
        )
        .eq("patient_id", patientId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data as PatientMessageRow[] | null) ?? [];
    },
    // Refetch on focus is OFF globally; messaging benefits from a tighter
    // refresh so a new reply appearing in another tab shows up quickly.
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/**
 * Count of inbound messages the patient hasn't opened yet. Used for the
 * nav-bar badge. Tiny query (just count), refetches on focus so the badge
 * clears when the patient opens Messages and we mutate read_at.
 */
export function useUnreadMessageCount(patientId: string | undefined) {
  return useQuery<number>({
    queryKey: ["messages-unread", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { count, error } = await supabase
        .from("patient_messages")
        .select("id", { count: "exact", head: true })
        .eq("patient_id", patientId!)
        .eq("direction", "inbound")
        .is("read_at", null);
      if (error) throw error;
      return count ?? 0;
    },
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
}

/**
 * Send a message from the patient to the care team. The edge function
 * persists the row + emails care@fortehealth.co (with Reply-To set to the
 * patient's email). On success we refetch the thread so the new message
 * appears immediately.
 */
export function useSendMessage(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; message_id: string; postmark_message_id?: string; routed_to?: string },
    Error,
    { body: string; subject?: string; category: Exclude<MessageCategory, "general"> }
  >({
    mutationFn: async ({ body, subject, category }) => {
      const { data, error } = await supabase.functions.invoke<{
        ok?: boolean;
        message_id?: string;
        postmark_message_id?: string;
        routed_to?: string;
        error?: string;
        detail?: string;
        name?: string;
      }>("patient-send-message", { body: { body, subject, category } });
      if (error) {
        // supabase-js wraps non-2xx as FunctionsHttpError with a generic
        // "Edge Function returned a non-2xx status code" message. The
        // actual response body is on error.context.response — read it
        // so the UI can surface a real reason instead of the wrapper.
        let detail = "";
        try {
          const ctx = (error as unknown as { context?: { response?: Response } })?.context;
          if (ctx?.response) {
            const cloned = ctx.response.clone();
            const txt = await cloned.text();
            detail = txt.length > 600 ? txt.slice(0, 600) + "…" : txt;
          }
        } catch {
          /* best-effort */
        }
        throw new Error(detail || error.message || "Failed to send message");
      }
      if (data?.error) throw new Error(data.detail ? `${data.error}: ${data.detail}` : data.error);
      if (!data?.ok || !data?.message_id) {
        throw new Error("Unexpected response from message send");
      }
      return {
        ok: true,
        message_id: data.message_id,
        postmark_message_id: data.postmark_message_id,
        routed_to: data.routed_to,
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages", patientId] });
    },
  });
}

/**
 * Mark all currently-unread inbound messages as read. Called when the
 * patient opens the Messages page. Tolerates the no-rows case silently.
 */
export function useMarkMessagesRead(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<number, Error, void>({
    mutationFn: async () => {
      if (!patientId) return 0;
      const { data, error } = await supabase
        .from("patient_messages")
        .update({ read_at: new Date().toISOString() })
        .eq("patient_id", patientId)
        .eq("direction", "inbound")
        .is("read_at", null)
        .select("id");
      if (error) throw error;
      return data?.length ?? 0;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages-unread", patientId] });
      qc.invalidateQueries({ queryKey: ["messages", patientId] });
    },
  });
}
